// Only editable content is rebased. Source links, figure links and record identities
// always come from the newest server document.
const blockFields = ["type", "title", "markdown", "review_reason", "needs_review"];
const questionFields = ["prompt_md", "answer_md", "solution_md"];
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const copy = (value) => value === undefined ? undefined : JSON.parse(JSON.stringify(value));
const order = (value) => (value?.blocks || []).map((block) => block.id);
const fieldLabels = { type: "分類", title: "標題", markdown: "內容", review_reason: "待核對說明", needs_review: "核對狀態", prompt_md: "題目", answer_md: "答案", solution_md: "詳解" };

const submissionLanded = (before, submitted, remote) => {
  if (!before || !submitted || !remote || submitted.id !== remote.id) return false;
  if (!same(before.title, submitted.title) && !same(submitted.title, remote.title)) return false;
  if (!same(order(before), order(submitted)) && !same(order(submitted), order(remote))) return false;
  const old = new Map(before.blocks.map((block) => [block.id, block]));
  const current = new Map(remote.blocks.map((block) => [block.id, block]));
  return submitted.blocks.every((block) => {
    const a = old.get(block.id), b = current.get(block.id);
    return a && b && blockFields.every((key) => same(a[key], block[key]) || same(block[key], b[key]))
      && questionFields.every((key) => same(a.question?.[key], block.question?.[key]) || same(block.question?.[key], b.question?.[key]));
  });
};

export function mergeMaterialDraft(base, local, remote, preferLocal = false) {
  const conflicts = [];
  if (!base || !local || !remote || base.id !== remote.id || local.id !== remote.id) {
    return { document: local, conflicts: [{ field: "教材身分", local: local?.id, remote: remote?.id }] };
  }
  const merged = copy(remote);
  const mergeField = (target, key, before, mine, theirs, field) => {
    if (same(before, mine)) return;
    if (!same(before, theirs) && !same(mine, theirs)) {
      conflicts.push({ field, base: before, local: mine, remote: theirs });
      if (!preferLocal) return;
    }
    if (mine === undefined) delete target[key]; else target[key] = copy(mine);
  };
  mergeField(merged, "title", base.title, local.title, remote.title, "教材名稱");
  const oldBlocks = new Map(base.blocks.map((block) => [block.id, block]));
  const localBlocks = new Map(local.blocks.map((block) => [block.id, block]));
  const remoteIds = new Set(order(remote));
  for (const block of local.blocks) {
    if (!remoteIds.has(block.id) && !same(block, oldBlocks.get(block.id))) {
      conflicts.push({ field: `區塊 ${block.id} 已由後端移除`, local: block.title || block.markdown, remote: "已移除" });
    }
  }
  merged.blocks = remote.blocks.map((block, index) => {
    const before = oldBlocks.get(block.id), mine = localBlocks.get(block.id);
    if (!before || !mine) return copy(block);
    const result = copy(block);
    const label = `區塊 ${index + 1}${block.title ? `（${block.title}）` : ""}`;
    for (const key of blockFields) mergeField(result, key, before[key], mine[key], block[key], `${label} · ${fieldLabels[key]}`);
    if (!same(before.question, mine.question)) {
      result.question = { ...block.question };
      for (const key of questionFields) mergeField(result.question, key, before.question?.[key], mine.question?.[key], block.question?.[key], `${label} · ${fieldLabels[key]}`);
    }
    return result;
  });
  if (!same(order(base), order(local))) {
    const identicalIds = same([...order(local)].sort(), [...order(remote)].sort());
    if (!identicalIds || (!same(order(base), order(remote)) && !same(order(local), order(remote)))) {
      conflicts.push({ field: "區塊順序", local: order(local), remote: order(remote) });
    }
    if (identicalIds && (preferLocal || same(order(base), order(remote)) || same(order(local), order(remote)))) {
      const entries = new Map(merged.blocks.map((block) => [block.id, block]));
      merged.blocks = order(local).map((id) => entries.get(id));
    }
  }
  return { document: merged, conflicts };
}

export function materialDraftOwner(authToken) {
  try {
    const encoded = String(authToken).split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(window.atob(encoded));
    const identity = claims.staff_account || claims.user_id || claims.sub;
    if (identity !== undefined && identity !== null) return encodeURIComponent(JSON.stringify([claims.iss || "", claims.staff_account ? "staff" : "user", String(identity)]));
  } catch { /* Opaque tokens are isolated by their complete value, never stored. */ }
  let first = 2166136261, second = 5381;
  for (const character of String(authToken)) { first = Math.imul(first ^ character.charCodeAt(0), 16777619); second = Math.imul(second, 33) ^ character.charCodeAt(0); }
  return `token-${first >>> 0}-${second >>> 0}`;
}

export const materialDraftKey = (owner, id, writer) => `material-document-draft-v2:${owner}:${id}${writer ? `:writer:${writer}` : ""}`;
let writerSequence = 0;
const newWriter = () => window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${++writerSequence}-${Math.random().toString(36).slice(2)}`;
const backupAckKey = (key, value) => {
  let first = 2166136261, second = 5381;
  for (let index = 0; index < value.length; index += 1) { first = Math.imul(first ^ value.charCodeAt(index), 16777619); second = Math.imul(second, 33) ^ value.charCodeAt(index); }
  return `material-document-draft-ack-v1:${encodeURIComponent(key)}:${first >>> 0}-${second >>> 0}-${value.length}`;
};
const statusError = (message) => new Error(message || "教材草稿尚未儲存，請稍後重試。");

// One session per authenticated editor; at most one PATCH is in flight. Network
// failures are reconciled with GET before retrying because PATCH may have landed.
export function createMaterialDraftSession({ owner, save, fetchJob, storage, delay = 750, onJob = () => {}, writer = newWriter() }) {
  let state = { job: null, document: null, revision: 0, dirty: false, status: "idle", error: "", conflicts: [], backupError: false, recoveryBranches: [], recoveryRequired: false };
  let base = null, token = 0, timer, inFlight = null, needsReconcile = false, uncertainSubmission = null, disposed = false;
  let pendingAcknowledgements = [];
  const listeners = new Set();
  const emit = () => listeners.forEach((listener) => listener({ ...state }));
  const acknowledge = (backups) => {
    for (const backup of backups) {
      try { if (backup.ackKey) storage?.setItem(backup.ackKey, JSON.stringify({ processedAt: Date.now() })); } catch { /* Keep the original branch recoverable if acknowledgement cannot be saved. */ }
    }
  };
  const persist = () => {
    if (!state.job) return;
    try {
      // Independent keys avoid a read/modify/write race between separate tabs.
      // A clean tab may clear its own checkpoint, never another writer's draft.
      if (state.recoveryRequired) return;
      const key = materialDraftKey(owner, state.job.id, writer);
      if (!state.dirty) storage?.removeItem(key);
      else storage?.setItem(key, JSON.stringify({ version: 2, writer, id: state.job.id, baseDocument: base, baseRevision: state.revision, document: state.document, submission: uncertainSubmission, updatedAt: Date.now() }));
      state.backupError = false;
    } catch { state.backupError = true; }
  };
  const schedule = () => {
    clearTimeout(timer);
    if (!disposed && state.dirty && !state.conflicts.length && !inFlight) {
      timer = setTimeout(() => { flush().catch(() => {}); }, delay);
    }
  };
  const adoptRemote = (next, notify = true) => {
    if (state.job && next.id !== state.job.id) return;
    if (next.revision < state.revision) return;
    if (state.recoveryRequired) { state.job = next; emit(); if (notify) onJob(next); return; }
    if (uncertainSubmission && next.revision > state.revision && submissionLanded(uncertainSubmission.base, uncertainSubmission.document, next.document)) {
      base = copy(uncertainSubmission.document);
      uncertainSubmission = null;
    }
    const merged = state.dirty ? mergeMaterialDraft(base, state.document, next.document) : { document: next.document, conflicts: [] };
    // On genuine conflict retain every local field for review, never silently
    // replace it with the server's conflicting text.
    if (merged.conflicts.length) {
      state = { ...state, job: next, conflicts: merged.conflicts, status: "conflict", error: "後端內容已有更新，請核對衝突欄位。" };
      persist(); emit(); if (notify) onJob(next); return;
    }
    base = copy(next.document);
    state = { ...state, job: next, document: merged.document, revision: next.revision || 0, conflicts: [], error: "", dirty: !same(merged.document, next.document) };
    state.status = state.dirty ? "pending" : "saved";
    if (!state.dirty) { acknowledge(pendingAcknowledgements); pendingAcknowledgements = []; }
    persist(); emit(); if (notify) onJob(next);
  };
  const perform = async (options, epoch) => {
    let attempts = 0;
    while (!disposed && token === epoch && state.dirty) {
      if (state.conflicts.length) throw statusError(state.error);
      if (needsReconcile) {
        const fresh = await fetchJob(state.job.id);
        if (disposed || token !== epoch) return null;
        if (uncertainSubmission && submissionLanded(uncertainSubmission.base, uncertainSubmission.document, fresh.job.document)) base = copy(uncertainSubmission.document);
        adoptRemote(fresh.job); needsReconcile = false;
        uncertainSubmission = null;
        if (state.conflicts.length) throw statusError(state.error);
        if (!state.dirty) break;
      }
      const submitted = copy(state.document), submittedBase = copy(base), submittedRevision = state.revision, id = state.job.id;
      uncertainSubmission = { base: submittedBase, document: submitted };
      persist();
      state.status = "saving"; state.error = ""; emit();
      try {
        const result = await save(id, submitted, submittedRevision, options);
        if (disposed || token !== epoch) return result.job;
        if (state.conflicts.length) throw statusError(state.error);
        const newest = state.job.revision > result.job.revision ? state.job : result.job;
        // Three-way merge keeps keystrokes that arrived after this request began.
        const merged = mergeMaterialDraft(submitted, state.document, newest.document);
        if (merged.conflicts.length) {
          state = { ...state, job: newest, status: "conflict", conflicts: merged.conflicts, error: "儲存期間內容已有更新，請核對。" };
          persist(); emit(); throw statusError(state.error);
        }
        base = copy(newest.document);
        uncertainSubmission = null;
        state = { ...state, job: newest, revision: newest.revision || 0, document: merged.document,
          dirty: !same(merged.document, newest.document), conflicts: [], error: "", status: same(merged.document, newest.document) ? "saved" : "pending" };
        acknowledge(pendingAcknowledgements); pendingAcknowledgements = [];
        persist(); emit(); onJob(newest);
      } catch (failure) {
        if (disposed || token !== epoch) return null;
        if (failure.status === 409) {
          const latest = failure.data?.job;
          if (!latest) {
            state = { ...state, status: "conflict", conflicts: [{ field: "後端版本", local: "目前編輯", remote: "請重新讀取核對" }], error: failure.message };
            persist(); emit(); throw failure;
          }
          adoptRemote(latest);
          uncertainSubmission = null;
          if (state.conflicts.length || ++attempts > 2) throw failure;
          continue;
        }
        if (state.conflicts.length) throw failure;
        needsReconcile = true;
        uncertainSubmission = { base: submittedBase, document: submitted };
        state.status = "offline"; state.error = failure.message || "連線暫時中斷";
        persist(); emit(); throw failure;
      }
    }
    return state.job;
  };
  const flush = (options = {}) => {
    clearTimeout(timer);
    if (disposed) return Promise.resolve(state.job);
    if (state.conflicts.length) return Promise.reject(statusError(state.error));
    if (inFlight) {
      const epoch = token;
      return inFlight.then(() => !disposed && token === epoch && state.dirty ? flush(options) : state.job);
    }
    if (!state.dirty || !state.job || !state.document) return Promise.resolve(state.job);
    const epoch = token;
    inFlight = perform(options, epoch).catch((failure) => {
      if (!disposed && epoch === token && !state.conflicts.length && state.status !== "offline") {
        state.status = "offline"; state.error = failure.message; persist(); emit();
      }
      throw failure;
    }).finally(() => { if (token === epoch) inFlight = null; });
    return inFlight;
  };
  return {
    snapshot: () => ({ ...state }),
    activate() { disposed = false; },
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    load(next, restore = true) {
      token += 1; clearTimeout(timer); inFlight = null; needsReconcile = false; uncertainSubmission = null;
      pendingAcknowledgements = [];
      base = copy(next.document);
      state = { job: next, document: next.document || null, revision: next.revision || 0, dirty: false, status: "saved", error: "", conflicts: [], backupError: false, recoveryBranches: [], recoveryRequired: false };
      if (restore && next.document) {
        try {
          const prefix = materialDraftKey(owner, next.id);
          const keys = storage?.keys ? storage.keys() : Array.from({ length: storage?.length || 0 }, (_, index) => storage.key(index));
          // The un-suffixed key is supported only for recovering older backups.
          const backups = [...new Set([prefix, ...keys.filter((key) => key?.startsWith(`${prefix}:writer:`))])].map((key) => {
            try {
              const raw = storage?.getItem(key) || "null", value = JSON.parse(raw), ackKey = backupAckKey(key, raw);
              return value && !storage?.getItem(ackKey) && { ...value, storageKey: key, ackKey };
            } catch { return null; }
          }).filter((value) => value?.version === 2 && value.id === next.id && value.document?.id === next.document.id && value.baseDocument?.id === next.document.id);
          let combined = copy(next.document); const conflicts = [], branches = [];
          for (const cached of backups) {
            const recoveredBase = cached.submission && submissionLanded(cached.submission.base, cached.submission.document, next.document) ? cached.submission.document : cached.baseDocument;
            const merged = mergeMaterialDraft(recoveredBase, cached.document, next.document);
            if (!merged.conflicts.length && same(merged.document, next.document)) { acknowledge([cached]); continue; }
            const content = merged.conflicts.length ? cached.document : merged.document;
            const joined = mergeMaterialDraft(next.document, content, combined);
            branches.push({ writer: cached.storageKey, ackKey: cached.ackKey, document: cached.document, baseDocument: recoveredBase, updatedAt: cached.updatedAt });
            conflicts.push(...merged.conflicts, ...joined.conflicts);
            if (!joined.conflicts.length) combined = joined.document;
            if (backups.length === 1 && merged.conflicts.length) {
              base = recoveredBase;
              combined = cached.document;
            }
          }
          if (branches.length) {
            pendingAcknowledgements = conflicts.length && branches.length > 1 ? [] : branches;
            state = { ...state, document: combined, dirty: conflicts.length > 0 || !same(combined, next.document),
              status: conflicts.length ? "conflict" : same(combined, next.document) ? "saved" : "pending", conflicts,
              recoveryRequired: branches.length > 1 && conflicts.length > 0,
              recoveryBranches: branches.length > 1 && conflicts.length > 0 ? branches : [],
              error: conflicts.length ? "找到不同分頁留下的草稿，請先核對。" : "" };
            if (branches.length === 1 && conflicts.length) base = copy(branches[0].baseDocument);
          }
        } catch { /* Corrupt optional backup must not hide the authoritative copy. */ }
      }
      persist(); emit(); schedule();
    },
    receive(next) { adoptRemote(next); schedule(); },
    edit(document) {
      if (!state.job || !document || state.recoveryRequired) return;
      state.document = document;
      state.dirty = !same(document, base) || Boolean(inFlight && uncertainSubmission && !same(document, uncertainSubmission.document));
      if (!state.conflicts.length) state.status = state.dirty ? "pending" : "saved";
      persist(); emit(); schedule();
    },
    flush,
    retry() { return flush(); },
    chooseRecovery(key) {
      const branch = state.recoveryBranches.find((item) => item.writer === key);
      if (!branch || !state.job?.document) return;
      const merged = mergeMaterialDraft(branch.baseDocument, branch.document, state.job.document);
      pendingAcknowledgements = [branch];
      base = copy(merged.conflicts.length ? branch.baseDocument : state.job.document);
      state = { ...state, document: merged.conflicts.length ? branch.document : merged.document, revision: state.job.revision,
        recoveryRequired: false, conflicts: merged.conflicts, dirty: merged.conflicts.length > 0 || !same(merged.document, state.job.document),
        status: merged.conflicts.length ? "conflict" : same(merged.document, state.job.document) ? "saved" : "pending", error: merged.conflicts.length ? "請核對這份草稿與後端的差異。" : "" };
      persist(); emit(); schedule();
    },
    resolveWithLocal() {
      if (state.recoveryRequired) return Promise.reject(statusError("請先選擇要核對的分頁草稿；其他份仍會保留。"));
      if (!state.conflicts.length || !state.job?.document) return Promise.resolve(state.job);
      const merged = mergeMaterialDraft(base, state.document, state.job.document, true);
      // Source removal requires loading the new document; it cannot be overridden.
      if (merged.conflicts.some((item) => item.field.includes("已由後端移除"))) return Promise.reject(statusError("區塊已由後端移除，請先下載本機草稿再重新載入。"));
      base = copy(state.job.document); state.document = merged.document; state.revision = state.job.revision;
      state.conflicts = []; state.dirty = !same(state.document, base); state.status = state.dirty ? "pending" : "saved";
      persist(); emit(); return flush();
    },
    dispose() { clearTimeout(timer); persist(); disposed = true; listeners.clear(); token += 1; inFlight = null; },
  };
}
